// ===========================================
// Protected Route Component
// ===========================================

import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/useAuth';
import { ReactNode } from 'react';
import { getDashboardPath, familyEventLinkRedirect } from '../utils/routing';
import ConsentGate from './parent/ConsentGate';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: Array<'ADMIN' | 'TEACHER' | 'PARENT' | 'STUDENT'>;
  requiredRole?: 'ADMIN' | 'TEACHER' | 'PARENT' | 'STUDENT';
  /**
   * Optional permission string in "resource.action" format.
   * Staff users (isStaff) are admitted to an ADMIN-gated route only (the staff view lives under
   * /admin): with this set, only if their staffRole.permissions includes it; without it, any
   * staff user (coarse-grained: AdminRouteGuard then gates each page). A staff role never opens
   * the TEACHER, PARENT or STUDENT shells for a login that is not that role (#310).
   *
   * Example: "reports.read", "people_teachers.write"
   */
  requiredPermission?: string;
  /**
   * #252: the parent shell only. A login that is not a parent following a family event email link
   * (`/parent/events/:id`) is sent to the admin event page, or to its dashboard with a notice,
   * instead of the bare dashboard. Every other path keeps the default redirect.
   */
  routeFamilyEventLinks?: boolean;
}

export default function ProtectedRoute({
  children,
  allowedRoles,
  requiredRole,
  requiredPermission,
  routeFamilyEventLinks,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const effectiveSlug = schoolSlug || 'music-n-me';

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={`/${effectiveSlug}/login`} state={{ from: location }} replace />;
  }

  // #442: a parent who has not agreed to the school's T&Cs sees the consent gate INSTEAD of
  // the page (and its layout), however the session started: login, 2FA, a refresh or an open
  // tab. The server enforces the same rule on every request (403 CONSENT_REQUIRED).
  if (user?.requiresConsentAcknowledgement) {
    return <ConsentGate />;
  }

  // Check role-based access
  const rolesToCheck = allowedRoles || (requiredRole ? [requiredRole] : undefined);

  if (rolesToCheck) {
    // SECURITY: when a role gate is in play we must have a loaded user object.
    // Treating a null user as "fall through" lets a stale/cached auth state
    // render a guarded route — observed 2026-04-30 when a SW-cached /auth/me
    // from a prior admin session served while a parent was logged in.
    if (!user) {
      return <Navigate to={`/${effectiveSlug}/login`} state={{ from: location }} replace />;
    }

    if (!rolesToCheck.includes(user.role)) {
      // Special case: Admin users who are also teachers can access teacher routes
      const isAdminWithTeacherAccess =
        user.role === 'ADMIN' && user.isAlsoTeacher && rolesToCheck.includes('TEACHER');

      // Staff users can access ADMIN routes when they have the required permission (any staff
      // user when none is named). Only the admin shell: this branch used to admit a staff
      // teacher to the /parent and /student shells too, whose data the server then refused (#310).
      const hasStaffAccess =
        user.isStaff === true &&
        rolesToCheck.includes('ADMIN') &&
        (requiredPermission
          ? user.staffRole?.permissions?.includes(requiredPermission) === true
          : true);

      const isAuthorized = isAdminWithTeacherAccess || hasStaffAccess;

      if (!isAuthorized) {
        const eventLink = routeFamilyEventLinks
          ? familyEventLinkRedirect(user, effectiveSlug, location.pathname)
          : null;
        if (eventLink) {
          return (
            <Navigate
              to={eventLink.to}
              replace
              state={eventLink.notice ? { routeNotice: eventLink.notice } : undefined}
            />
          );
        }
        // Redirect to user's dashboard if they don't have the required role/permission
        return <Navigate to={getDashboardPath(user.role, effectiveSlug, { isStaff: user.isStaff })} replace />;
      }
    }
  }

  return <>{children}</>;
}
